/**
 * Creative metadata form component
 * Collects metadata for a creative (objective, placement, audience, etc.)
 */

import * as React from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { CreativeMetadata } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface CreativeMetadataFormProps {
  defaultName?: string;
  onSubmit: (data: {
    name: string;
    metadata: CreativeMetadata;
    placements: { placement: string }[];
  }) => void;
  isLoading?: boolean;
}

interface FormData {
  name: string;
  objective: string;
  placements: string[];
  audience: string;
  product: string;
  isPromo: boolean;
}

interface FormErrors {
  name?: string;
  objective?: string;
  placement?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const OBJECTIVES = [
  { value: 'Awareness', label: 'Awareness' },
  { value: 'Consideration', label: 'Consideration' },
  { value: 'Performance', label: 'Performance' },
];

const PLACEMENTS = [
  { value: 'Feed', label: 'Feed', description: 'Main news feed' },
  { value: 'Reels', label: 'Reels', description: 'Short-form video' },
  { value: 'Stories', label: 'Stories', description: '24-hour ephemeral content' },
  { value: 'InStream', label: 'In-Stream', description: 'Video ads in longer content' },
  { value: 'Search', label: 'Search', description: 'Search results placement' },
  { value: 'Other', label: 'Other', description: 'Other placements' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function CreativeMetadataForm({
  defaultName = '',
  onSubmit,
  isLoading = false,
}: CreativeMetadataFormProps) {
  const [formData, setFormData] = React.useState<FormData>({
    name: defaultName,
    objective: '',
    placements: [],
    audience: '',
    product: '',
    isPromo: false,
  });

  const [errors, setErrors] = React.useState<FormErrors>({});

  // Update name when defaultName changes
  React.useEffect(() => {
    if (defaultName && !formData.name) {
      setFormData((prev) => ({ ...prev, name: defaultName }));
    }
  }, [defaultName, formData.name]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Creative name is required';
    }

    if (!formData.objective) {
      newErrors.objective = 'Please select a campaign objective';
    }

    if (formData.placements.length === 0) {
      newErrors.placement = 'Please select at least one placement';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Build placements array for API
    const placementsData = formData.placements.map(p => ({ placement: p }));

    const metadata: CreativeMetadata = {
      objective: formData.objective as 'Awareness' | 'Consideration' | 'Performance',
      // Keep legacy field for backward compatibility (primary placement)
      placement: formData.placements[0] as 'Reels' | 'Feed' | 'Stories' | undefined,
      ...(formData.audience && { audience: formData.audience }),
      ...(formData.product && { product: formData.product }),
      isPromo: formData.isPromo,
    };

    onSubmit({
      name: formData.name.trim(),
      metadata,
      placements: placementsData,
    });
  };

  const handleChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const value = e.target.type === 'checkbox'
      ? (e.target as HTMLInputElement).checked
      : e.target.value;

    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePlacementToggle = (placement: string) => {
    setFormData((prev) => {
      const isSelected = prev.placements.includes(placement);
      const newPlacements = isSelected
        ? prev.placements.filter(p => p !== placement)
        : [...prev.placements, placement];
      return { ...prev, placements: newPlacements };
    });

    // Clear error when user selects a placement
    if (errors.placement) {
      setErrors((prev) => ({ ...prev, placement: undefined }));
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Creative Name */}
      <Input
        label="Creative Name"
        type="text"
        value={formData.name}
        onChange={handleChange('name')}
        error={!!errors.name}
        errorMessage={errors.name}
        placeholder="Enter creative name"
        disabled={isLoading}
        autoFocus
      />

      {/* Campaign Objective */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-secondary">
          Campaign Objective <span className="text-danger">*</span>
        </label>
        <select
          value={formData.objective}
          onChange={handleChange('objective')}
          className="flex h-11 w-full rounded-md border border-border-subtle bg-surface px-4 text-md text-primary transition-all duration-base ease-standard focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isLoading}
        >
          <option value="">Select objective</option>
          {OBJECTIVES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.objective && (
          <p className="text-xs text-danger" role="alert">
            {errors.objective}
          </p>
        )}
      </div>

      {/* Ad Placements - Multi-select */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-secondary">
          Ad Placements <span className="text-danger">*</span>
        </label>
        <p className="text-xs text-muted mb-2">
          Select all placements where this creative will run
        </p>
        <div className="grid grid-cols-2 gap-2">
          {PLACEMENTS.map((option) => (
            <label
              key={option.value}
              className={`
                flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${formData.placements.includes(option.value)
                  ? 'border-brand bg-brand/5'
                  : 'border-border-subtle hover:border-border-strong'}
                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input
                type="checkbox"
                checked={formData.placements.includes(option.value)}
                onChange={() => handlePlacementToggle(option.value)}
                disabled={isLoading}
                className="mt-0.5 h-4 w-4 rounded border-border-default text-brand focus:ring-4 focus:ring-brand/12 focus:ring-offset-0"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-primary">{option.label}</span>
                {option.description && (
                  <p className="text-xs text-muted mt-0.5">{option.description}</p>
                )}
              </div>
            </label>
          ))}
        </div>
        {errors.placement && (
          <p className="text-xs text-danger" role="alert">
            {errors.placement}
          </p>
        )}
      </div>

      {/* Audience/Targeting */}
      <Input
        label="Audience/Targeting (Optional)"
        type="text"
        value={formData.audience}
        onChange={handleChange('audience')}
        placeholder="e.g., Prospecting, Retargeting"
        helperText="Describe the target audience for this creative"
        disabled={isLoading}
      />

      {/* Product Name */}
      <Input
        label="Product Name (Optional)"
        type="text"
        value={formData.product}
        onChange={handleChange('product')}
        placeholder="e.g., Premium Subscription"
        helperText="Specify the product featured in this creative"
        disabled={isLoading}
      />

      {/* Is Promo/Offer */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="isPromo"
          checked={formData.isPromo}
          onChange={handleChange('isPromo')}
          disabled={isLoading}
          className="h-4 w-4 rounded border-border-default text-brand focus:ring-4 focus:ring-brand/12 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <label htmlFor="isPromo" className="text-sm font-medium text-secondary">
          This creative includes a promotional offer
        </label>
      </div>

      {/* Submit Button */}
      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="flex-1"
          loading={isLoading}
          disabled={isLoading}
        >
          Create Creative
        </Button>
      </div>
    </form>
  );
}
